import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../../../redux/store";
import {
  Currency,
  CurrencyPayload,
  CurrencyState,
} from "./types/currency-types";

const API_BASE = process.env.REACT_APP_API_URL || "";

async function handleResp(res: Response) {
  const text = await res.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (!res.ok) {
    const msg =
      (payload && (payload.message || payload.error)) ||
      res.statusText ||
      "Request failed";
    throw new Error(msg);
  }
  return payload;
}

export const fetchCurrencies = createAsyncThunk<
  Currency[],
  { page?: number; perPage?: number; search?: string } | undefined,
  { rejectValue: string; state: RootState }
>(
  "currencies/fetch",
  async (params = { page: 1, perPage: 50 }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const token = (state as any).auth?.token;
    if (!token) return rejectWithValue("Unauthorized");

    try {
      const url = new URL(`${API_BASE}/currencies`);
      url.searchParams.set("page", String(params.page ?? 1));
      url.searchParams.set("per_page", String(params.perPage ?? 50));
      if (params.search) url.searchParams.set("search", params.search);

      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload: any = await handleResp(res);
      if (Array.isArray(payload)) return payload;
      if (payload?.data?.data && Array.isArray(payload.data.data))
        return payload.data.data;
      if (payload?.data && Array.isArray(payload.data)) return payload.data;
      return [];
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to fetch currencies");
    }
  }
);

export const createCurrency = createAsyncThunk<
  Currency,
  CurrencyPayload,
  { rejectValue: string; state: RootState }
>("currencies/create", async (payload, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/currencies`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data: any = await handleResp(res);
    return data?.data ?? data;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to create currency");
  }
});

export const updateCurrency = createAsyncThunk<
  Currency,
  { id: number } & Partial<CurrencyPayload>,
  { rejectValue: string; state: RootState }
>(
  "currencies/update",
  async ({ id, ...payload }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const token = (state as any).auth?.token;
    if (!token) return rejectWithValue("Unauthorized");

    try {
      const res = await fetch(`${API_BASE}/currencies/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data: any = await handleResp(res);
      return data?.data ?? data;
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to update currency");
    }
  }
);

export const deleteCurrency = createAsyncThunk<
  number,
  number,
  { rejectValue: string; state: RootState }
>("currencies/delete", async (id, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/currencies/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    await handleResp(res);
    return id;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to delete currency");
  }
});

const initialState: CurrencyState = {
  items: [],
  loading: false,
  saving: false,
  error: null,
};

const currencySlice = createSlice({
  name: "currencies",
  initialState,
  reducers: {
    clearCurrencyError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrencies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrencies.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchCurrencies.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch currencies";
      })
      .addCase(createCurrency.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createCurrency.fulfilled, (state, action) => {
        state.saving = false;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createCurrency.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to create currency";
      })
      .addCase(updateCurrency.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateCurrency.fulfilled, (state, action) => {
        state.saving = false;
        const idx = state.items.findIndex((c) => c.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
      })
      .addCase(updateCurrency.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to update currency";
      })
      .addCase(deleteCurrency.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteCurrency.fulfilled, (state, action) => {
        state.saving = false;
        state.items = state.items.filter((c) => c.id !== action.payload);
      })
      .addCase(deleteCurrency.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to delete currency";
      });
  },
});

export const { clearCurrencyError } = currencySlice.actions;

export const selectCurrencies = (state: RootState) => state.currencies?.items;
export const selectCurrenciesLoading = (state: RootState) =>
  state.currencies?.loading;
export const selectCurrenciesSaving = (state: RootState) =>
  state.currencies?.saving;
export const selectCurrenciesError = (state: RootState) =>
  state.currencies?.error;

export default currencySlice.reducer;
